/**
 * A helper class that encapsulates cursor-based pagination state.
 *
 * This class is intentionally lightweight and stateful so that a single
 * instance can be used for one paginator (e.g., one table). Multiple
 * instances can coexist in a view when multiple tables need independent
 * pagination states.
 *
 * The implementation mirrors the cursor/paging logic used in several
 * claim pages (lost outbound, refund never returned, reanalysis, etc.)
 * but is kept generic so it can be reused across features.
 */
export class CursorPaginator {
  private pageSizeValue: number;
  private currentPageIndexValue: number;
  private paginationCursorsValue: (string | null | undefined)[];
  private hasMorePagesValue: boolean;

  constructor(initialPageSize = 10) {
    this.pageSizeValue = initialPageSize;
    this.currentPageIndexValue = 0;
    this.paginationCursorsValue = [null];
    this.hasMorePagesValue = false;
  }

  get pageSize(): number {
    return this.pageSizeValue;
  }

  get currentPageIndex(): number {
    return this.currentPageIndexValue;
  }

  get hasMorePages(): boolean {
    return this.hasMorePagesValue;
  }

  /**
   * Returns the cursor that should be sent to the backend for the given page.
   *
   * - Page 0 should return `null`, which signals "start from the beginning".
   * - Pages beyond the known cursor list return `undefined` (meaning don't call).
   */
  getCursorForPage(pageIndex: number): string | null | undefined {
    if (pageIndex < 0) return undefined;
    if (pageIndex === 0) return null;
    return this.paginationCursorsValue[pageIndex] ?? undefined;
  }

  /**
   * Resets pagination to the first page.
   */
  reset(): void {
    this.currentPageIndexValue = 0;
    this.paginationCursorsValue = [null];
    this.hasMorePagesValue = false;
  }

  /**
   * Updates the page size and resets the paging state.
   */
  setPageSize(size: number): void {
    this.pageSizeValue = size;
    this.reset();
  }

  /**
   * Returns true if the paginator has a cursor for the given page.
   */
  canNavigateToPage(pageIndex: number): boolean {
    if (pageIndex < 0) return false;
    if (pageIndex === 0) return true;
    return pageIndex < this.paginationCursorsValue.length;
  }

  /**
   * Updates internal cursor list based on a response cursor.
   *
   * @param responseCursor The next-page cursor returned by the backend.
   * @param pageIndex Optional page index that produced the response. When
   * omitted, the current page index is assumed.
   */
  updateAfterResponse(
    responseCursor: string | null | undefined,
    pageIndex?: number,
  ): void {
    const index = pageIndex ?? this.currentPageIndexValue;
    const newCursors = [...this.paginationCursorsValue];

    if (responseCursor) {
      this.hasMorePagesValue = true;
      const nextPageIndex = index + 1;
      if (newCursors.length <= nextPageIndex) {
        newCursors.push(responseCursor);
      } else {
        newCursors[nextPageIndex] = responseCursor;
      }
    } else {
      this.hasMorePagesValue = false;
    }

    this.paginationCursorsValue = newCursors;
  }

  /**
   * Calculates the paginator length value (for use in MatPaginator) based on the
   * current state and the number of items returned on the current page.
   */
  calculatePaginatorLength(currentItemsCount: number): number {
    if (currentItemsCount < 0) currentItemsCount = 0;

    const pageIndex = this.currentPageIndexValue;
    const size = this.pageSizeValue;

    // If there are more pages, show one extra page worth of items to enable
    // the pagination control (same pattern used in existing claim pages).
    if (this.hasMorePagesValue) {
      return pageIndex * size + currentItemsCount + size;
    }

    // No more pages, just show the exact count.
    return pageIndex * size + currentItemsCount;
  }

  /**
   * Sets the current page index. It is expected that callers validate
   * canNavigateToPage() first.
   */
  setPageIndex(pageIndex: number): void {
    if (pageIndex < 0) return;
    this.currentPageIndexValue = pageIndex;
  }
}
